import { t } from "ttag";

import { AppointmentDetailsCard } from "@/features/consultations/components";
import { useCancelAppointment } from "@/features/consultations/hooks";
import type { Appointment } from "@/features/consultations/models";
import { LogoLoader } from "@/shared/components";
import { Button, Dialog, ShieldWarningIcon } from "@/shared/ui";

interface CancelAppointmentModalProps {
  show: boolean;
  onClose: VoidFunction;
  appointment: Appointment;
}

export const CancelAppointmentModal = ({
  show,
  onClose,
  appointment,
}: CancelAppointmentModalProps) => {
  const cancelAppointmentMutation = useCancelAppointment();

  const handleCancelAppointment = () => {
    cancelAppointmentMutation.mutate(
      {
        id: appointment.id,
        subscriptionId: appointment.subscriptionId ?? undefined,
        appointmentType: appointment.type,
      },
      {
        onSuccess: onClose,
      },
    );
  };

  return (
    <Dialog.Root open={show} onOpenChange={onClose}>
      <Dialog.Content>
        <Dialog.Header icon={ShieldWarningIcon}>
          <Dialog.Title>{t`Are you sure you want to cancel?`}</Dialog.Title>

          <Dialog.Description>
            {t`Canceling your appointment with will mean losing your spot. However, you can always book a new one at a time that works for you.`}
          </Dialog.Description>
        </Dialog.Header>

        <AppointmentDetailsCard appointment={appointment} />

        <Dialog.Footer>
          <Button
            onClick={onClose}
            size="lg"
            variant="secondary"
            disabled={cancelAppointmentMutation.isPending}
          >
            {t`No, keep consultation`}
          </Button>
          <Button
            onClick={handleCancelAppointment}
            size="lg"
            disabled={cancelAppointmentMutation.isPending}
          >
            {t`Yes, cancel`}
          </Button>
        </Dialog.Footer>

        <LogoLoader show={cancelAppointmentMutation.isPending} fullscreen />
      </Dialog.Content>
    </Dialog.Root>
  );
};
